import { TransactionOpeningRoutes } from "@/modules/template/common/enum";
import type { ITemplate } from "@/modules/template/common/types";
import { useGetTemplatesQuery } from "@/modules/template/services";
import { CloudflareAsset } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { ActionItem, CloudFlareImage, ListContentWrapper } from "@/shared/components";
import { useScroll } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";


const MAX_COUNT = 7;

export const Templates = () => {
  const navigate = useNavigate();
  const {
    data: templateList,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetTemplatesQuery({}, {
    refetchOnMountOrArgChange: true,
  });

  const {
    isAtEnd,
    isAtStart,
    scrollContainerRef,
    onHorizontalScroll,
  } = useScroll();

  const onOpenTemplate = (template: ITemplate) => {
    if (TransactionOpeningRoutes[template.transactionType]) {
      navigate(`${TransactionOpeningRoutes[template.transactionType]}?templateId=${template.id}`);
    }
  };

  const templates = useMemo(() => {
    if (templateList && templateList.length > MAX_COUNT) {
      return templateList.slice(0, MAX_COUNT);
    }
    return templateList;
  }, [templateList]);

  return (
    <div className="full-width templates">
      <ListContentWrapper
        isError={isError}
        isFetching={isFetching}
        isEmpty={!templateList?.length}
        error={error as IError}
        refetch={refetch}
      >
        <div className="position-relative">
          <div ref={scrollContainerRef} className="service-items-wrapper mb-24">
            {!isAtStart && (
              <div className="arrow-wrapper left-overlay">
                <Button
                  onClick={() => onHorizontalScroll("left")}
                  type="secondary"
                  size="medium"
                  data-testid="template-left-scroll"
                  data-ga-id="template-left-scroll"
                  data-ga-action="Templates widget left scroll button"
                  iconProps={{
                    Component: (props: ISVGIconProps) => (
                      <CloudFlareImage {...props} path={CloudflareAsset.ArrowShortLeft}/>
                    ),
                  }}
                />
              </div>
            )}
            <div className="full-width flexbox">
              {templates?.map((template) => (
                <div key={template.id} className="pr-24 pb-24">
                  <ActionItem
                    className="template-item"
                    description={template.description}
                    title={template.name}
                    icon={template.transactionType as CloudflareAsset}
                    onClick={() => onOpenTemplate(template)}
                  />
                </div>
              ))}
            </div>
            {!isAtEnd && (
              <div className="arrow-wrapper right-overlay">
                <Button
                  onClick={() => onHorizontalScroll("right")}
                  type="secondary"
                  size="medium"
                  data-testid="template-right-scroll"
                  data-ga-id="template-right-scroll"
                  data-ga-action="Templates widget right scroll button"
                  iconProps={{
                    Component: (props: ISVGIconProps) => (
                      <CloudFlareImage {...props} path={CloudflareAsset.ArrowShortRight}/>
                    ),
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </ListContentWrapper>
    </div>
  );
};
